// @flow
import { format } from "date-fns"
import moment from "moment"
import * as R from "ramda"

import type { LanguageInfo } from "common/records/LanguageInfo"
import type { Translations } from "common/services/intl/pureTranslate"
import SearchDate from "common/records/SearchDate"
import type { SearchDate as NewSearchDate } from "common/records/SearchDate/SearchDate"
import type { Journey as NewJourney } from "common/records/Journey/Journey"
import type Journey from "common/records/Journey"
import type PassengersCount from "common/records/PassengersCount"
import type Options from "common/records/Options"
import * as JourneyUtils from "common/records/Journey/utils"
import { DATE_FORMAT } from "common/consts/date"
import { DATE_TYPES } from "common/records/SearchDate/SearchDate"
import { isSingleDate } from "common/records/SearchDate/utils"
import { setSerializedRefFlags } from "../../../booking/services/tracking/helpers/refFlags"
import pureTranslate from "../intl/pureTranslate"
import { makeQuery } from "../query/queries"
import { hashToPassengers } from "../../../booking/services/reservation/passengersCountHash"
import { distance } from "./formatters"

const ndash = "–" // \u2013

type BookingUrlOptions = {
  urlPrefix?: string,
  direct?: boolean,
  refFlags?: Array<string>,
  promocode?: string,
  price?: number,
  setBackToSearchUrl?: boolean,
}

export default class Formatter {
  translations: Translations

  languageInfo: LanguageInfo

  constructor(translations: Translations, languageInfo: LanguageInfo) {
    this.translations = translations
    this.languageInfo = languageInfo
  }

  _translate(key: string, params?: { [key: string]: string }, options?: Options) {
    return pureTranslate(key, params, { translations: this.translations, ...options })
  }

  formatDistance(distanceKm: number): string {
    return distance(this.languageInfo, distanceKm)
  }

  formatSearchDate(searchDate: SearchDate) {
    const momentLang = this.languageInfo.moment
    moment.locale(momentLang) // warning: this switches moment language globally - on server it can affect other users, so do it before all server rendered dates

    const { dateFormat } = this.languageInfo

    switch (searchDate.mode) {
      case "single":
        return moment.utc(searchDate.get("from")).format(dateFormat)
      case "timeToStay":
        return searchDate.get("minStayDays") === searchDate.get("maxStayDays")
          ? `${searchDate.get("minStayDays")} ${this._translate(__("form_search.of_nights"))}`
          : `${searchDate.get("minStayDays")} ${ndash} ${searchDate.get(
              "maxStayDays",
            )} ${this._translate(__("form_search.of_nights"))}`
      case "interval":
      case "month": {
        const toDateString = searchDate.get("to")
          ? moment.utc(searchDate.get("to")).format(dateFormat)
          : "_"
        return `${moment.utc(searchDate.get("from")).format(dateFormat)} ${ndash} ${toDateString}`
      }
      case "anytime":
        return this._translate(__("form_search.anytime"))
      case "noReturn":
        return this._translate(__("form_search.no_return_label"))
      default:
        return searchDate.mode
    }
  }

  formatNewSearchDate(searchDate: NewSearchDate, shortDate: boolean = false): string {
    const momentLang = this.languageInfo.moment
    moment.locale(momentLang) // warning: this switches moment language globally - on server it can affect other users, so do it before all server rendered dates

    const dateFormat = shortDate ? this.languageInfo.dateFormatShort : this.languageInfo.dateFormat

    switch (searchDate.type) {
      case DATE_TYPES.DATE_RANGE: {
        if (isSingleDate(searchDate)) {
          // I would love to use date-fns format, but it only has some of locale needed
          return moment(searchDate.from).format(dateFormat)
        }
        const toDateString = searchDate.to ? moment(searchDate.to).format(dateFormat) : "_"
        return `${moment(searchDate.from).format(dateFormat)} ${ndash} ${toDateString}`
      }

      case DATE_TYPES.TIME_TO_STAY:
        return searchDate.min === searchDate.max
          ? `${searchDate.min} ${this._translate(__("form_search.of_nights"))}`
          : `${searchDate.min} ${ndash} ${searchDate.max} ${this._translate(
              __("form_search.of_nights"),
            )}`

      case DATE_TYPES.ANYTIME:
        return this._translate(__("form_search.anytime"))
      case DATE_TYPES.NO_RETURN:
        return this._translate(__("form_search.no_return_label"))
      default:
        return searchDate.type
    }
  }

  makeSearchDateUrl(searchDate: NewSearchDate) {
    switch (searchDate.type) {
      case DATE_TYPES.DATE_RANGE: {
        const from = format(searchDate.from, DATE_FORMAT)
        const to = format(searchDate.to, DATE_FORMAT)
        return `${from}${from !== to ? `_${to}` : ""}`
      }
      case DATE_TYPES.TIME_TO_STAY:
        return `${searchDate.min}${searchDate.min !== searchDate.max ? `-${searchDate.max}` : ""}`
      case DATE_TYPES.ANYTIME:
        return this._translate(__("seo.url.anytime"))
      case DATE_TYPES.NO_RETURN:
        return this._translate(__("seo.url.no-return"))
      default:
        return ""
    }
  }

  static makeAlternativeSearchUrl(
    journey: Journey,
    passengersCount: ?string,
    options?: { noBags?: boolean } = {},
  ) {
    let urlParams = {}

    if (journey.isMulticity()) {
      /**
       * Multicity query has form: "multicity=-;london_gb~dusseldorf_de~2017-06-03;--~newyork_usa~--
       * "-"  default multicity trip
       * "--" default multicity parameter (origin|destination|date)
       * "~"  separates multicity parameters (origin~destination~date)
       * ";"  separates multicity trips (trip1;trip2)
       * ","  separates multiplace in departure|destination (london_gb,paris_fr) - won't be used here in booking
       */
      const multicityCityPairs = journey.getMulticityCityPairs(true).toArray()
      const departureDates = journey.getMulticityTripsDates().toArray()

      urlParams = {
        multicity: multicityCityPairs
          .map(
            (pair, index) =>
              `${pair.from}~${pair.to}~${departureDates[index].departure}_${departureDates[index].arrival}`,
          )
          .join(";"),
      }
    } else {
      // Shared for return & oneway
      urlParams = {
        departure: journey
          .getDeparture()
          .getIn(["when", "local"])
          .format("DD-MM-YYYY"),
        from: journey.getDeparture().getIn(["where", "city"]),
        to: journey.getArrival().getIn(["where", "city"]),
      }

      // Special for return
      if (journey.isReturn()) {
        urlParams = {
          ...urlParams,
          return: journey
            .get("trips")
            .last()
            .getArrival()
            .getIn(["when", "local"])
            .format("DD-MM-YYYY"),
        }
      }
    }

    if (passengersCount) {
      const passengers = hashToPassengers(passengersCount)

      if (passengers.adult) urlParams = { ...urlParams, adults: passengers.adult }
      if (passengers.child) urlParams = { ...urlParams, children: passengers.child }
      if (passengers.teen) urlParams = { ...urlParams, teens: passengers.teen }
      if (passengers.infant) urlParams = { ...urlParams, infants: passengers.infant }
    }
    return `/searchDeep${makeQuery(
      R.merge(urlParams, { ...options, multicity_noBags: options.noBags }),
    )}`
  }

  static makeBookingUrl(
    passengers: PassengersCount,
    journey: Journey,
    {
      urlPrefix,
      direct,
      refFlags,
      promocode,
      price,
      setBackToSearchUrl = true,
    }: BookingUrlOptions = {},
  ) {
    const urlParams: any = setSerializedRefFlags("ref", refFlags, {
      passengers: [
        passengers.get("adults"),
        passengers.get("children"),
        passengers.get("infants"),
      ].join("-"),
      price: price || journey.getPrice(),
      token: journey.get("bookingToken"),
      backToSearchUrl: setBackToSearchUrl ? window.location.href : undefined,
    })
    // NOTE: should we avoid "direct" and just use "refFlags" instead?
    // https://gitlab.skypicker.com/frontend/frontend/issues/4672
    if (direct) {
      urlParams.direct = "true"
    }
    if (promocode) {
      urlParams.promocode = promocode
    }

    return `${urlPrefix || ""}/booking${makeQuery(urlParams)}`
  }

  // TODO: remove the prefix when the old one is no more used
  // eslint-disable-next-line camelcase
  static newJourney_makeBookingUrl(
    passengers: PassengersCount,
    journey: NewJourney,
    { urlPrefix, direct, refFlags, promocode, price }: BookingUrlOptions = {},
  ) {
    const urlParams: any = setSerializedRefFlags("ref", refFlags, {
      passengers: [
        passengers.get("adults"),
        passengers.get("children"),
        passengers.get("infants"),
      ].join("-"),
      price: price || JourneyUtils.getPrice(journey),
      token: journey.bookingToken,
      backToSearchUrl: window.location.href,
    })
    // NOTE: should we avoid "direct" and just use "refFlags" instead?
    // https://gitlab.skypicker.com/frontend/frontend/issues/4672
    if (direct) {
      urlParams.direct = "true"
    }
    if (promocode) {
      urlParams.promocode = promocode
    }

    return `${urlPrefix || ""}/booking${makeQuery(urlParams)}`
  }
}
